import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { AiOutlineWifi, AiOutlineStop, AiOutlineGlobal } from "react-icons/ai";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./PingPage.css";

const DNS_SERVERS = [
  { name: "System Default", value: "" },
  { name: "Google (8.8.8.8)", value: "8.8.8.8" },
  { name: "Cloudflare (1.1.1.1)", value: "1.1.1.1" },
  { name: "Quad9 (9.9.9.9)", value: "9.9.9.9" },
  { name: "OpenDNS (208.67.222.222)", value: "208.67.222.222" },
];

const COMMON_PORTS = [
  { name: "Auto (Try All)", value: "" },
  { name: "HTTP (80)", value: "80" },
  { name: "HTTPS (443)", value: "443" },
  { name: "HTTP Alt (8080)", value: "8080" },
  { name: "Custom (9000)", value: "9000" },
  { name: "SSH (22)", value: "22" },
  { name: "Custom Port", value: "custom" },
];

const PingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [domain, setDomain] = useState(searchParams.get("domain") || "");

  // Get DNS from URL param or default to Cloudflare for testing
  const getDnsFromParam = () => {
    const dnsParam = searchParams.get("dns");
    if (dnsParam) {
      const found = DNS_SERVERS.find(
        (s) =>
          s.value === dnsParam ||
          s.name.toLowerCase().includes(dnsParam.toLowerCase())
      );
      if (found) return found;
    }
    // Default to Cloudflare for testing
    return DNS_SERVERS.find((s) => s.value === "1.1.1.1") || DNS_SERVERS[0];
  };

  const [dnsServer, setDnsServer] = useState(getDnsFromParam());

  // Initialize port from URL or default to empty (auto)
  const getPortFromParam = () => {
    const portParam = searchParams.get("port");
    if (!portParam) return "";
    // Check if it's a common port
    const found = COMMON_PORTS.find((p) => p.value === portParam);
    if (found) return found.value;
    // Otherwise it's a custom port
    return "custom";
  };

  const [port, setPort] = useState(getPortFromParam());
  const [customPort, setCustomPort] = useState(
    searchParams.get("port") &&
      !COMMON_PORTS.find((p) => p.value === searchParams.get("port"))
      ? searchParams.get("port")
      : ""
  );
  const [isPinging, setIsPinging] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [pingHistory, setPingHistory] = useState([]);
  const [wasmReady, setWasmReady] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const stopRef = useRef(false);
  const wasmModuleRef = useRef(null);
  const goInstanceRef = useRef(null);

  useEffect(() => {
    // Load WebAssembly module
    const loadWasm = async () => {
      try {
        // Load wasm_exec.js
        if (!window.Go) {
          const script = document.createElement("script");
          script.src = "/ping-server/wasm_exec.js";
          script.onload = async () => {
            await loadWasmModule();
          };
          document.head.appendChild(script);
        } else {
          await loadWasmModule();
        }
      } catch (err) {
        console.error("Failed to load WASM:", err);
        setError("Failed to load ping module");
      }
    };

    const loadWasmModule = async () => {
      try {
        const go = new window.Go();
        goInstanceRef.current = go; // Keep reference to prevent GC

        const wasmModule = await WebAssembly.instantiateStreaming(
          fetch("/ping-server/ping.wasm"),
          go.importObject
        );

        // Run Go program asynchronously (it blocks forever, so we don't await it)
        // The main() function blocks with <-make(chan bool), keeping the program alive
        setTimeout(() => {
          go.run(wasmModule.instance).catch((err) => {
            console.error("Go runtime error:", err);
            setError("Go runtime error: " + err.message);
          });
        }, 0);

        // Poll for the function to be available
        let attempts = 0;
        const maxAttempts = 50;
        while (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          if (window.pingHost && typeof window.pingHost === "function") {
            wasmModuleRef.current = window.pingHost;
            setWasmReady(true);
            return;
          }
          attempts++;
        }

        throw new Error("pingHost function not found after initialization");
      } catch (err) {
        console.error("WASM instantiation error:", err);
        setError("Failed to initialize ping module: " + err.message);
      }
    };

    loadWasm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update DNS server when URL param changes
  useEffect(() => {
    const newDns = getDnsFromParam();
    if (newDns.value !== dnsServer.value) {
      setDnsServer(newDns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-start ping when WASM is ready and domain is in URL
  useEffect(() => {
    const urlDomain = searchParams.get("domain");
    if (
      urlDomain &&
      urlDomain.trim() &&
      wasmReady &&
      !isPinging &&
      wasmModuleRef.current
    ) {
      // Only set domain if it's different to avoid unnecessary re-renders
      if (domain !== urlDomain) {
        setDomain(urlDomain);
      }
      // Small delay to ensure everything is initialized
      const timer = setTimeout(() => {
        if (wasmModuleRef.current) {
          startPing(urlDomain);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasmReady, searchParams]);

  const startPing = async (targetDomain = domain) => {
    if (!targetDomain || !targetDomain.trim()) {
      setError("Please enter a domain or IP address");
      return;
    }

    if (
      port === "custom" &&
      (!customPort || parseInt(customPort) < 1 || parseInt(customPort) > 65535)
    ) {
      setError("Please enter a valid port number (1-65535)");
      return;
    }

    if (!wasmReady || !wasmModuleRef.current) {
      setError("Ping module not ready yet");
      return;
    }

    // Update URL with selected parameters
    const pingCount = 10;
    const host = targetDomain.trim();
    const portToUse = port === "custom" ? customPort : port;

    const newParams = new URLSearchParams();
    newParams.set("domain", host);
    if (portToUse) {
      newParams.set("port", portToUse);
    }
    if (dnsServer.value) {
      newParams.set("dns", dnsServer.value);
    }
    newParams.set("count", pingCount.toString());
    setSearchParams(newParams, { replace: true });

    setIsPinging(true);
    setPingHistory([]);
    setStats(null);
    setError(null);
    setCurrentStatus("Initializing ping...");
    setLogs([]); // Clear logs when starting new ping
    stopRef.current = false;

    const pingResults = [];
    const pingTimes = [];

    try {
      await new Promise((resolve, reject) => {
        const progressCallback = (result) => {
          try {
            const seq = result.sequence;
            const success = result.success;
            const time = result.time || 0;
            const errorMsg = result.error || "";

            if (success) {
              setCurrentStatus(`Ping #${seq}: ${time}ms - Success`);
              pingTimes.push(time);
              setPingHistory((prev) =>
                [
                  ...prev,
                  {
                    time: seq,
                    value: time,
                  },
                ].slice(-50)
              );
            } else {
              setCurrentStatus(`Ping #${seq}: Failed - ${errorMsg}`);
            }

            pingResults.push({ sequence: seq, success, time, error: errorMsg });
          } catch (err) {
            console.error("Progress callback error:", err);
          }
        };

        const summaryCallback = (summary) => {
          try {
            const sent = summary.sent || pingCount;
            const received = summary.received || pingTimes.length;
            const lost = summary.lost || sent - received;
            const lossPercent = summary.lossPercent || (lost / sent) * 100;
            const min =
              summary.min ||
              (pingTimes.length > 0 ? Math.min(...pingTimes) : 0);
            const max =
              summary.max ||
              (pingTimes.length > 0 ? Math.max(...pingTimes) : 0);
            const avg =
              summary.avg ||
              (pingTimes.length > 0
                ? Math.round(
                    pingTimes.reduce((a, b) => a + b, 0) / pingTimes.length
                  )
                : 0);

            setStats({
              sent,
              received,
              lost,
              lossPercent: Math.round(lossPercent * 10) / 10,
              min: Math.round(min),
              max: Math.round(max),
              avg: Math.round(avg),
            });

            setCurrentStatus(
              `Completed: ${received}/${sent} successful, ${Math.round(
                avg
              )}ms avg`
            );
            resolve();
          } catch (err) {
            console.error("Summary callback error:", err);
            reject(err);
          }
        };

        setCurrentStatus(
          `Starting ping to ${host}${
            dnsServer.value ? ` via DNS ${dnsServer.name}` : ""
          }...`
        );

        // Log callback for detailed Golang logs
        const logCallback = showLogs
          ? (logMsg) => {
              try {
                const timestamp = new Date().toLocaleTimeString();
                setLogs((prev) =>
                  [...prev, `[${timestamp}] ${logMsg}`].slice(-200)
                );
              } catch (err) {
                console.error("Log callback error:", err);
              }
            }
          : null;

        // Determine which port to use
        const portToUse = port === "custom" ? customPort : port;

        wasmModuleRef.current(
          host,
          pingCount,
          dnsServer.value || "",
          portToUse || "",
          progressCallback,
          summaryCallback,
          logCallback || null
        );
      });
    } catch (err) {
      console.error("Ping error:", err);
      setError(err.message || "Failed to ping domain");
      setCurrentStatus("Error occurred");
    } finally {
      setIsPinging(false);
    }
  };

  const stopPing = () => {
    stopRef.current = true;
    setIsPinging(false);
  };

  const Chart = ({ data, color }) => {
    // Ensure data is an array and has at least one point
    const chartData =
      Array.isArray(data) && data.length > 0 ? data : [{ time: 0, value: 0 }];

    return (
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={100}>
          <LineChart data={chartData}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#111"
              vertical={false}
            />
            <XAxis dataKey="time" hide />
            <YAxis
              hide
              domain={["auto", "auto"]}
              padding={{ top: 10, bottom: 10 }}
            />
            <Tooltip
              isAnimationActive={false}
              contentStyle={{
                backgroundColor: "#000",
                border: "1px solid #222",
                fontSize: "10px",
              }}
              itemStyle={{ color: "#fff" }}
              labelStyle={{ display: "none" }}
            />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="ping-container dashboard-style">
      <div className="monitor-header">
        <h1>Ping Test</h1>
      </div>

      <div className="ping-card">
        <div className="test-header">
          <div className="test-title">
            <AiOutlineWifi /> PING
          </div>
          <div className="test-result">
            {stats ? `${stats.avg}ms` : isPinging ? "..." : "--"}
          </div>
        </div>

        <Chart data={pingHistory} color="#fff" />

        {currentStatus && (
          <div className="status-display">
            <span className="status-text">{currentStatus}</span>
          </div>
        )}

        <div className="input-group">
          <label>Target Domain / IP</label>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !isPinging) {
                startPing();
              }
            }}
            placeholder="e.g., google.com or 8.8.8.8"
            disabled={isPinging}
            className="domain-input"
          />
        </div>

        <div className="input-group">
          <label>
            <AiOutlineGlobal /> DNS Server{" "}
            <span
              style={{ fontSize: "0.7rem", color: "#666", fontStyle: "italic" }}
            >
              (informational only in browser)
            </span>
          </label>
          <select
            value={dnsServer.value}
            onChange={(e) => {
              const selected =
                DNS_SERVERS.find((s) => s.value === e.target.value) ||
                DNS_SERVERS[0];
              setDnsServer(selected);
            }}
            disabled={isPinging}
            className="domain-input"
          >
            {DNS_SERVERS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label>Port</label>
          <select
            value={port}
            onChange={(e) => {
              setPort(e.target.value);
              if (e.target.value !== "custom") {
                setCustomPort("");
              }
            }}
            disabled={isPinging}
            className="domain-input"
          >
            {COMMON_PORTS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {port === "custom" && (
          <div className="input-group">
            <label>Custom Port Number</label>
            <input
              type="number"
              value={customPort}
              onChange={(e) => {
                const value = e.target.value;
                if (
                  value === "" ||
                  (parseInt(value) > 0 && parseInt(value) <= 65535)
                ) {
                  setCustomPort(value);
                }
              }}
              placeholder="Enter port (1-65535)"
              disabled={isPinging}
              className="domain-input"
              min="1"
              max="65535"
            />
          </div>
        )}

        {stats && (
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Sent</span>
              <span className="stat-value">{stats.sent}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Received</span>
              <span className="stat-value">{stats.received}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Lost</span>
              <span className="stat-value">
                {stats.lost} ({stats.lossPercent}%)
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Min</span>
              <span className="stat-value">{stats.min}ms</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Max</span>
              <span className="stat-value">{stats.max}ms</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Avg</span>
              <span className="stat-value">{stats.avg}ms</span>
            </div>
          </div>
        )}

        <div className="input-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showLogs}
              onChange={(e) => setShowLogs(e.target.checked)}
              disabled={isPinging}
            />
            <span>Show Golang Logs</span>
          </label>
        </div>

        {showLogs && logs.length > 0 && (
          <div className="logs-container">
            <div className="logs-header">
              <span>Golang Execution Logs</span>
              <button
                onClick={() => setLogs([])}
                className="clear-logs-btn"
                title="Clear logs"
              >
                Clear
              </button>
            </div>
            <div className="logs-content">
              {logs.map((log, idx) => (
                <div key={idx} className="log-line">
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="btn-group-row">
          <button
            className="start-button"
            onClick={() => startPing()}
            disabled={isPinging || !domain.trim() || !wasmReady}
          >
            {!wasmReady
              ? "Loading..."
              : isPinging
              ? "Pinging..."
              : "Start Ping"}
          </button>
          {isPinging && (
            <button className="stop-action-btn" onClick={stopPing} title="Stop">
              <AiOutlineStop />
            </button>
          )}
        </div>
      </div>

      {error && <div className="network-error">{error}</div>}
    </div>
  );
};

export default PingPage;
